/**
 * SessionManager - Persists shell session (tabs, active tab) to localStorage
 *
 * Features:
 * - Automatic save on tab changes (debounced)
 * - Per-user session storage
 * - Handles corrupted data gracefully
 * - Validates saved apps against available apps
 *
 * Storage key format: focus:session:<userId>
 */

import { broadcastSync } from './BroadcastSync'

const STORAGE_KEY_PREFIX = 'focus:session'
const SAVE_DEBOUNCE_MS = 2000
const SESSION_VERSION = 1

export interface SavedSession {
  version: number
  userId: string
  tabs: SavedTab[]
  activeTabId: string | null
  lastSaved: string
}

export interface SavedTab {
  tabId: string
  appId: string
  instanceId: string
  title: string
  isPinned?: boolean
}

class SessionManager {
  private userId: string = ''
  private saveTimeout: NodeJS.Timeout | null = null
  private pendingSave: { tabs: SavedTab[]; activeTabId: string | null } | null = null

  constructor() {
    // Flush any pending debounced save before page unloads
    window.addEventListener('beforeunload', () => this.flush())
  }

  /**
   * Initialize session manager with user ID
   * Must be called after authentication
   */
  initialize(userId: string): void {
    this.userId = userId
  }

  /**
   * Load saved session from localStorage
   * Returns null if no session exists or if data is corrupted
   */
  load(): SavedSession | null {
    if (!this.userId) {
      console.warn('[SessionManager] Cannot load session: userId not set')
      return null
    }

    try {
      const key = `${STORAGE_KEY_PREFIX}:${this.userId}`
      const raw = localStorage.getItem(key)

      if (!raw) {
        return null
      }

      const session = JSON.parse(raw) as SavedSession

      // Validate session structure
      if (!this.isValidSession(session)) {
        console.warn('[SessionManager] Invalid session data, ignoring')
        this.clear()
        return null
      }

      // Check version compatibility
      if (session.version !== SESSION_VERSION) {
        console.warn('[SessionManager] Session version mismatch, clearing')
        this.clear()
        return null
      }

      return session
    } catch (error) {
      console.error('[SessionManager] Error loading session:', error)
      this.clear()
      return null
    }
  }

  /**
   * Save session to localStorage immediately
   */
  save(tabs: SavedTab[], activeTabId: string | null): void {
    if (!this.userId) {
      console.warn('[SessionManager] Cannot save session: userId not set')
      return
    }

    try {
      const key = `${STORAGE_KEY_PREFIX}:${this.userId}`
      const session: SavedSession = {
        version: SESSION_VERSION,
        userId: this.userId,
        tabs,
        activeTabId,
        lastSaved: new Date().toISOString(),
      }

      localStorage.setItem(key, JSON.stringify(session))

      // Notify other windows
      broadcastSync.postMessage({
        type: 'SESSION_SAVED',
        timestamp: session.lastSaved,
      })
    } catch (error) {
      console.error('[SessionManager] Error saving session:', error)
    }
  }

  /**
   * Schedule a save operation (debounced)
   * Use this instead of save() to avoid excessive writes.
   * Pending saves are flushed automatically on beforeunload.
   */
  scheduleSave(tabs: SavedTab[], activeTabId: string | null): void {
    this.pendingSave = { tabs, activeTabId }

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }

    this.saveTimeout = setTimeout(() => {
      this.flush()
    }, SAVE_DEBOUNCE_MS)
  }

  /**
   * Flush any pending debounced save immediately
   */
  flush(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
      this.saveTimeout = null
    }
    if (this.pendingSave) {
      this.save(this.pendingSave.tabs, this.pendingSave.activeTabId)
      this.pendingSave = null
    }
  }

  /**
   * Clear saved session
   */
  clear(): void {
    if (!this.userId) return

    try {
      const key = `${STORAGE_KEY_PREFIX}:${this.userId}`
      localStorage.removeItem(key)
    } catch (error) {
      console.error('[SessionManager] Error clearing session:', error)
    }
  }

  /**
   * Validate session data structure
   */
  private isValidSession(session: unknown): session is SavedSession {
    if (!session || typeof session !== 'object') return false

    const s = session as SavedSession

    return (
      typeof s.version === 'number' &&
      typeof s.userId === 'string' &&
      Array.isArray(s.tabs) &&
      (s.activeTabId === null || typeof s.activeTabId === 'string') &&
      typeof s.lastSaved === 'string' &&
      s.tabs.every(this.isValidTab)
    )
  }

  /**
   * Validate tab data structure
   */
  private isValidTab(tab: unknown): tab is SavedTab {
    if (!tab || typeof tab !== 'object') return false

    const t = tab as SavedTab

    return (
      typeof t.tabId === 'string' &&
      typeof t.appId === 'string' &&
      typeof t.instanceId === 'string' &&
      typeof t.title === 'string'
    )
  }
}

// Singleton instance
export const sessionManager = new SessionManager()
